import { useNavigate } from "react-router-dom";
import { useState, useContext } from "react";
import Navbar from "../Navbar";
import axios from "axios";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import plutonicSmallName from "../../assets/images/plutonicSmallName.svg";
import plutonicLogo from "../../assets/images/plutonicLogo.svg";
import { AuthContext } from "../../context/AuthContext";
import { useAgeValidation } from "../../hooks/useAgeValidation";
import Env from "../../runtimeEnv";

const states = ["Georgia"];

function RegisterTherapyOnly() {
  const {
    currentNickname,
    setCurrentNickname,
    handleLogout,
    PlutonicuserID,
    setPlutonicuserID,
    userType,
    setUserType,
    currentFormType,
    setCurrentFormType,
    email,
    setEmail,
  } = useContext(AuthContext);

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    age: "",
    state: "",
    password: "",
    userType: "Client",
  });

  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { ageError, validateAge } = useAgeValidation(18);

  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasUppercase: false,
    hasLowercase: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "age" && !/^\d*$/.test(value)) return;
    if (name === "age") validateAge(value);

    if (name === "password") {
      const val = value;
      setPasswordRequirements({
        minLength: val.length >= 8,
        hasNumber: /[0-9]/.test(val),
        hasSpecialChar: /[!@#$%^&*]/.test(val),
        hasUppercase: /[A-Z]/.test(val),
        hasLowercase: /[a-z]/.test(val),
      });
    }

    setFormData((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (parseInt(formData.age, 10) < 18) {
      setError("You must be at least 18 years old to register.");
      return;
    }

    const allRequirementsMet =
      Object.values(passwordRequirements).every(Boolean);
    if (!allRequirementsMet) {
      setError("Password does not meet all requirements.");
      return;
    }

    if (formData.password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setError(""); 

    try {
      const response = await axios.post(
        `${Env.REACT_APP_PYTHON_API_URL}/api/register`,
        formData,
        { withCredentials: true }
      );

      if (response.status === 201) {
        const {
          PlutonicuserID,
          userType,
          email: registeredEmail,
        } = response.data;

        setPlutonicuserID(PlutonicuserID);
        setUserType(userType);
        setEmail(registeredEmail);

        localStorage.setItem("PlutonicuserID", PlutonicuserID);
        localStorage.setItem("userType", userType);
        localStorage.setItem("email", registeredEmail);

        setSuccess("Registration successful! Redirecting to survey...");
        setTimeout(() => navigate("/survey"), 2000);
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Registration failed.";
      setError(msg);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center px-4 py-8"
      style={{
        background: "linear-gradient(to bottom, #24004266 40%, #4FCBE6 100%)",
      }}
    >
      <Navbar
        currentNickname={currentNickname}
        handleLogout={handleLogout}
        setCurrentFormType={setCurrentFormType}
        showButtons={false}
        PlutonicuserID={PlutonicuserID}
      />

      <form
        onSubmit={handleSubmit}
        autoComplete="off"
        className="mt-[100px] w-full max-w-sm bg-black/80 rounded-3xl shadow-lg p-5 sm:p-6 text-white text-center"
      >
        <h2 className="text-xl font-bold mb-4">Register for Therapy Only</h2>

        <input
          name="firstName"
          placeholder="First Name"
          value={formData.firstName}
          onChange={handleChange}
          autoComplete="off"
          className="w-full mb-3 h-[48px] px-4 bg-transparent border border-gray-400 rounded-full text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#22AAEE]"
          required
        />

        <input
          name="lastName"
          placeholder="Last Name"
          value={formData.lastName}
          onChange={handleChange}
          autoComplete="off"
          className="w-full mb-3 h-[48px] px-4 bg-transparent border border-gray-400 rounded-full text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#22AAEE]"
          required
        />

        <input
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          autoComplete="off"
          className="w-full mb-3 h-[48px] px-4 bg-transparent border border-gray-400 rounded-full text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#22AAEE]"
          required
        />

        <div className="mb-3">
          <input
            name="age"
            placeholder="Age"
            value={formData.age}
            onChange={handleChange}
            autoComplete="off"
            className="w-full h-[48px] px-4 bg-transparent border border-gray-400 rounded-full text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#22AAEE]"
            required
          />
          {ageError && (
            <p className="text-red-400 text-xs mt-1 text-left px-2">
              {ageError}
            </p>
          )}
        </div>

        <div className="mb-3 relative z-10">
          <div
            className={`w-full h-[48px] px-4 bg-transparent border border-gray-400 rounded-full text-white text-sm placeholder-gray-500 
                focus:outline-none focus:border-[#22AAEE] flex items-center justify-between cursor-pointer ${
                  dropdownOpen ? "ring-2 ring-[#22AAEE]" : ""
                }`}
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <span className={formData.state ? "" : "text-gray-500"}>
              {formData.state || "Select State"}
            </span>
            <svg
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          {dropdownOpen && (
            <ul className="absolute w-full mt-1 bg-white text-black rounded-lg shadow-md max-h-60 overflow-y-auto">
              {states.map((state) => (
                <li
                  key={state}
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, state }));
                    setDropdownOpen(false);
                  }}
                  className="px-4 py-2 hover:bg-[#22AAEE] hover:text-white cursor-pointer text-sm"
                >
                  {state}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mb-3 relative">
          <input
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            autoComplete="new-password"
            className="w-full h-[48px] px-4 bg-transparent border border-gray-400 rounded-full text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#22AAEE]"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>

        {formData.password.length > 0 &&
          !Object.values(passwordRequirements).every(Boolean) && (
            <div className="text-left text-xs text-gray-300 mt-1 mb-3 px-2">
              <p className="mb-1 text-white">
                Password must meet the following:
              </p>
              <ul className="list-disc ml-4 space-y-1">
                <li
                  className={
                    passwordRequirements.minLength
                      ? "text-green-400"
                      : "text-red-400"
                  }
                >
                  At least 8 characters
                </li>
                <li
                  className={
                    passwordRequirements.hasNumber
                      ? "text-green-400"
                      : "text-red-400"
                  }
                >
                  Contains a number
                </li>
                <li
                  className={
                    passwordRequirements.hasSpecialChar
                      ? "text-green-400"
                      : "text-red-400"
                  }
                >
                  Contains a special character (!@#$%^&*)
                </li>
                <li
                  className={
                    passwordRequirements.hasUppercase
                      ? "text-green-400"
                      : "text-red-400"
                  }
                >
                  Contains an uppercase letter
                </li>
                <li
                  className={
                    passwordRequirements.hasLowercase
                      ? "text-green-400"
                      : "text-red-400"
                  }
                >
                  Contains a lowercase letter
                </li>
              </ul>
            </div>
          )}

        <input
          name="confirmPassword"
          type="password"
          placeholder="Confirm Password"
          value={confirmPassword}
          autoComplete="new-password"
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full mb-3 h-[48px] px-4 bg-transparent border border-gray-400 rounded-full text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#22AAEE]"
          required
        />

        {error && <p className="text-red-400 text-sm mb-2">{error}</p>}
        {success && <p className="text-green-400 text-sm mb-2">{success}</p>}
        
        <div className="flex items-start text-xs text-white gap-2 mb-6 text-left">
          <input
            type="checkbox"
            name="agree"
            checked={formData.agree}
            onChange={handleChange}
            className="mt-1 w-4 h-4 accent-white"
            required
          />
          <label htmlFor="agree">
            I agree to the{" "}
            <a
              href="/terms"
              className="underline font-semibold text-white"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>
            ,{" "}
            <a
              href="/privacy"
              className="underline font-semibold text-white"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            , and understand that this service is{" "}
            <span className="font-semibold">not for emergency use</span>.
          </label>

        </div>

        <button
          type="submit"
          className="w-full h-[48px] px-4 border border-[#22AAEE] text-[#22AAEE] font-bold rounded-full transition duration-200 hover:bg-[#22AAEE] hover:text-white text-sm"
        >
          SIGN UP
        </button>

        <div className="mt-2 flex items-center justify-center space-x-2">
          <img
            src={plutonicLogo}
            alt="Plutonic Logo"
            className="w-[50px] h-[30px]"
          />
          <img
            src={plutonicSmallName}
            alt="company name"
            className="w-20 h-20"
          />
        </div>
      </form>
    </div>
  );
}

export default RegisterTherapyOnly;
